import React, { useState } from 'react';
import Box from '@mui/material/Box';
import useStyles from './styles';
import { PostType as PostTypeEnum, PostCardInfo } from 'types/post';
import { PostGrid } from 'organisms';
import { PageHeader } from 'molecules';
import { PaginationOptions, QueryResult, ScrollToTop } from '@elevator/ui';

/**
 * PostType Component
 *
 * Displays posts filtered by type (Submitted, Sponsored, Premiere, Promoted) in a grid layout
 * with pagination support. Handles display limit state for progressive loading
 * and fetchMore for pagination.
 *
 * Features:
 * - Semantic `<main>` with `data-layout="post-type"` and vertical `gap` between blocks
 * - PageHeader for the type title and PostGrid for posts with pagination
 * - Load More functionality (cache-first, then fetchMore)
 * - Bottom ad via `withLayout` (`bottomAd`)
 * - Scroll to top button
 *
 * Used in:
 * - PostType layout index (rendered for type pages)
 *
 * @param props - Component props
 * @param props.type - Post type enum (Submitted, Sponsored, Premiere, Promoted)
 * @param props.posts - Array of post cards to display
 * @param props.variables - GraphQL query variables (type, limit, skip)
 * @param props.fetchMore - Apollo Client fetchMore function for pagination
 * @param props.loading - Whether initial data is loading
 * @param props.loadingMore - Whether more posts are being fetched
 * @param props.error - GraphQL query error
 * @param props.sectionTitle - Optional section title override
 * @returns PostType component with posts grid and pagination
 */
const PostType = (props: PostTypePropTypes) => {
  const { classes } = useStyles();
  const { type, posts, loading, loadingMore, error, fetchMore, variables, sectionTitle } =
    props;

  // Display limit state - resets when type changes (adjust during render)
  const [displayLimit, setDisplayLimit] = useState(variables.limit);
  const [prevType, setPrevType] = useState(type);
  const [revealAfterLength, setRevealAfterLength] = useState<number | null>(null);
  if (type !== prevType) {
    setPrevType(type);
    setDisplayLimit(variables.limit);
    setRevealAfterLength(null);
  }

  // Slice posts array based on displayLimit
  const allPosts = posts || [];
  const displayedPosts = allPosts.slice(0, displayLimit);

  // After fetchMore, reveal the next page once cache length grows
  if (revealAfterLength != null && allPosts.length > revealAfterLength) {
    setRevealAfterLength(null);
    setDisplayLimit((prev) => prev + variables.limit);
  }

  const onLoadMore = () => {
    // Check if more posts exist in cache
    if (allPosts.length > displayLimit) {
      // More posts available in cache - just increase display limit (instant)
      setDisplayLimit((prev) => prev + variables.limit);
    } else {
      // Need to fetch more posts; reveal once cache grows
      setRevealAfterLength(allPosts.length);
      fetchMore({
        variables: {
          limit: variables.limit,
          skip: allPosts.length,
          type,
        },
        updateQuery: (
          prev: { publishedPosts: PostCardInfo[] },
          { fetchMoreResult }: { fetchMoreResult?: { publishedPosts: PostCardInfo[] } },
        ) => {
          if (!fetchMoreResult) return prev;

          const newPosts = [...prev.publishedPosts, ...fetchMoreResult.publishedPosts];

          return {
            ...prev,
            publishedPosts: newPosts,
          };
        },
      });
    }
  };

  return (
    <>
      <Box component="main" className={classes.container} data-layout="PostType">
        <PageHeader title={sectionTitle ? sectionTitle : type} divider />
        <Box component="section" width="100%">
          <PostGrid
            posts={displayedPosts}
            onLoadMore={onLoadMore}
            error={error}
            isLoading={loading}
            loadingMore={loadingMore}
            adUnitPrefix="ELEVATOR-Mid"
          />
        </Box>
      </Box>
      <ScrollToTop visibleYOffset={70} />
    </>
  );
};

export type PostsByTypeQueryVariables = PaginationOptions & {
  type: PostTypeEnum;
};

export type PostTypePropTypes = QueryResult<
  'posts',
  PostCardInfo[],
  PostsByTypeQueryVariables
> & {
  type: PostTypeEnum;
  sectionTitle?: string;
};

export default PostType;
